import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Typography,
    Box,
    Tooltip,
    CircularProgress,
    useTheme,
    Grid,
    Card,
    CardContent,
    Alert,
} from '@mui/material';
import {
    Edit as EditIcon,
    Visibility as ViewIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    AccountTree as SubOrderIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    MoreHoriz as MoreHorizIcon,
    WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getServiceOrders } from '../../services/serviceOrders';
import LoadingScreen from '../LoadingScreen';
import PageContainer from '../PageContainer';
import { useAuth } from '../../contexts/AuthContext';
import usePagination from '../../hooks/usePagination';
import useSearch from '../../hooks/useSearch';
import SearchBar from '../common/SearchBar';
import KQSPLPagination from '../common/Pagination';
import { toast } from 'react-toastify';
import { PrimaryButton, SecondaryButton, DestructiveButton } from '../common/Buttons';
import CustomDropdown from '../common/CustomDropdown';
import { CustomTextField } from '../common/CustomTextFields';
import { formatDateTime, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';
import { getServiceOrderStatusChipColor, getServiceOrderPriorityChipColor, formatStatusLabel } from '../../constants/statusColors';
import { toastApiError } from '../../utils/errorHandler';
import { getPoStatus } from '../../utils/poUtils';

const DEFAULT_FILTERS = {
    status: '',
    priority: '',
    is_sub_service_order: 'false',
    po_status: '',
    ordering: '-created_at'
};

const ServiceOrderList = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Read po_status from BOTH navigation state (from Dashboard button) AND URL search param
    const getInitialPoStatus = () => {
        if (location.state?.po_status) return location.state.po_status;
        return new URLSearchParams(window.location.search).get('po_status') || '';
    };

    const initPoStatus = getInitialPoStatus();
    const initFilters = useMemo(() => {
        if (initPoStatus) {
            return {
                ...DEFAULT_FILTERS,
                is_sub_service_order: '', // Show all matching expired PO orders
                po_status: initPoStatus
            };
        }
        return DEFAULT_FILTERS;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [appliedFilters, setAppliedFilters] = useState(initFilters);
    const [draftFilters, setDraftFilters] = useState(initFilters);
    const [showFilters, setShowFilters] = useState(!!initPoStatus);
    const [statusOptions, setStatusOptions] = useState([]);
    const [priorityOptions, setPriorityOptions] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [expandedParents, setExpandedParents] = useState({});
    const isMounted = useRef(false);

    const toggleParentExpand = (parentDisplayName) => {
        setExpandedParents(prev => ({
            ...prev,
            [parentDisplayName]: !prev[parentDisplayName]
        }));
    };

    const {
        data: orders,
        loading,
        error,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        refresh,
        handleParamsChange
    } = usePagination(getServiceOrders, initFilters, 50);

    // Sync when location.state or URL query params change after mount
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        const poStatus = location.state?.po_status ||
            new URLSearchParams(location.search).get('po_status') || '';
        const next = {
            ...appliedFilters,
            is_sub_service_order: poStatus ? '' : (appliedFilters.is_sub_service_order || 'false'),
            po_status: poStatus
        };
        setAppliedFilters(next);
        setDraftFilters(next);
        setShowFilters(!!poStatus);
        handleParamsChange(next);
    }, [location.search, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check user permissions for service orders
    const canViewServiceOrders = user?.role?.permissions?.service_orders?.view;
    const canEditServiceOrders = user?.role?.permissions?.service_orders?.change;
    const canAddServiceOrders = user?.role?.permissions?.service_orders?.add;

    // Mark initial load as done once data arrives for the first time
    useEffect(() => {
        if (initialLoading && !loading) {
            setInitialLoading(false);
        }
    }, [loading, initialLoading]);

    // Organize orders hierarchically: parents first, then their children grouped below
    const organizedOrders = useMemo(() => {
        if (!orders || orders.length === 0) {
            return orders || [];
        }

        // Separate parents and sub-orders
        const parentOrders = orders.filter(o => !o.is_sub_service_order);
        const subOrders = orders.filter(o => o.is_sub_service_order);

        // Create a map of parent_id -> sub-orders
        const subOrdersByParent = {};
        subOrders.forEach(sub => {
            const parentId = sub.parent_order_id;
            if (parentId) {
                if (!subOrdersByParent[parentId]) {
                    subOrdersByParent[parentId] = [];
                }
                subOrdersByParent[parentId].push(sub);
            }
        });

        // Build the organized list: parent followed by its children
        let result = [];
        const processedSubOrderIds = new Set();

        parentOrders.forEach(parent => {
            // Add parent with metadata about children
            const children = subOrdersByParent[parent.id] || [];
            const displayName = parent.display_name || parent.name || `SO-${parent.so_sequence || parent.id}`;
            const isExpanded = expandedParents[displayName];
            const maxVisibleChildren = isExpanded ? 5 : 0;
            const visibleChildren = children.slice(0, maxVisibleChildren);
            const hasMoreChildren = children.length > maxVisibleChildren;

            result.push({
                ...parent,
                _childCount: children.length,
                _isParentInGroup: children.length > 0,
                _displayName: displayName
            });

            // Mark ALL children as processed to prevent orphan display
            children.forEach(child => processedSubOrderIds.add(child.id));

            // Add only visible children after parent
            visibleChildren.forEach((child, index) => {
                const isLastVisible = index === visibleChildren.length - 1;
                result.push({
                    ...child,
                    _isChildInGroup: true,
                    _isLastChild: isLastVisible && !hasMoreChildren,
                    _childIndex: index,
                    _showMoreAfterThis: isLastVisible && hasMoreChildren,
                    _remainingChildCount: hasMoreChildren ? children.length - maxVisibleChildren : 0,
                    _parentId: parent.id,
                    _parentDisplayName: displayName
                });
            });
        });

        // Add any orphan sub-orders (whose parent is not in current page)
        subOrders.forEach(sub => {
            if (!processedSubOrderIds.has(sub.id)) {
                result.push({
                    ...sub,
                    _isOrphanChild: true
                });
            }
        });

        // Apply client-side PO status filtering if requested
        if (appliedFilters.po_status) {
            result = result.filter(order => {
                const poStat = getPoStatus(order.po_expiry_date, order.po_number);
                return poStat.status === appliedFilters.po_status;
            });
        }

        return result;
    }, [orders, expandedParents, appliedFilters.po_status]);

    // Load filter options
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const { getServiceOrderStatuses, getServiceOrderPriorities } = await import('../../services/serviceOrders');
                const [statuses, priorities] = await Promise.all([
                    getServiceOrderStatuses(),
                    getServiceOrderPriorities()
                ]);
                setStatusOptions(statuses || []);
                setPriorityOptions(priorities || []);
            } catch (error) {
                console.error('Error loading filter options:', error);
                toastApiError(toast.error, error, 'Failed to load filter options', 'view filter options');
            }
        };
        loadFilterOptions();
    }, []);

    // Draft filter change (form only; API not called until Apply)
    const handleDraftFilterChange = (field, value) => {
        setDraftFilters((prev) => ({ ...prev, [field]: value }));
    };

    // Apply draft filters to API
    const applyFilters = () => {
        const next = { ...draftFilters, ordering: appliedFilters.ordering };
        if (searchQuery) next.search = searchQuery;
        setAppliedFilters(next);
        handleParamsChange(next);
    };

    // Sort applies immediately (separate from filters)
    const handleSortChange = (ordering) => {
        const next = { ...appliedFilters, ordering };
        setAppliedFilters(next);
        handleParamsChange(next);
    };

    // Clear all filters and sort to default
    const clearFilters = () => {
        setDraftFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        resetQuery();
        if (location.search.includes('po_status') || location.state?.po_status) {
            navigate('/service-orders', { replace: true, state: {} });
        }
        handleParamsChange(DEFAULT_FILTERS);
    };

    // When opening filter panel, sync draft to applied so form shows current state
    const handleToggleFilters = () => {
        if (!showFilters) setDraftFilters(appliedFilters);
        setShowFilters((prev) => !prev);
    };

    // Search hook with SO- prefix stripping and appliedFilters state sync
    const getSOSearchParams = useCallback((query) => {
        let searchValue = query;
        if (searchValue && searchValue.toUpperCase().startsWith('SO-')) {
            searchValue = searchValue.slice(3);
        }
        const next = { ...appliedFilters, search: searchValue || undefined };
        if (!searchValue) delete next.search;
        setAppliedFilters(next);
        return next;
    }, [appliedFilters]);

    const { searchQuery, searchInputRef, handleSearchChange, handleClearSearch, resetQuery } = useSearch(
        handleParamsChange,
        { getSearchParams: getSOSearchParams }
    );

    if (initialLoading && loading) {
        return <LoadingScreen message="Loading service orders…" />;
    }

    if (error) {
        return (
            <PageContainer>
                <Typography color="error" align="center">
                    {error}
                </Typography>
            </PageContainer>
        );
    }

    let columns = [
        {
            id: 'id',
            label: 'ID',
        },
        {
            id: 'service_type',
            label: 'Service Type',
        },
        {
            id: 'company',
            label: 'Company',
        },
        {
            id: 'status',
            label: 'Status',
        },
        {
            id: 'priority',
            label: 'Priority',
        },
        {
            id: 'technician',
            label: 'Technician',
        },
        {
            id: 'scheduled',
            label: 'Scheduled',
        },
        {
            id: 'actions',
            label: 'Actions',
        }
    ];

    // Filter columns based on permissions, not hardcoded role
    if (!canViewServiceOrders) {
        columns = columns.filter((column) => column.id !== 'service_type' && column.id !== 'priority' && column.id !== 'scheduled');
    }

    // Render a single order row
    const renderOrderRow = (order) => {
        const isChildInGroup = order._isChildInGroup;
        const isOrphanChild = order._isOrphanChild;
        const isParentWithChildren = order._isParentInGroup;
        const childCount = order._childCount || 0;

        const poStat = getPoStatus(order.po_expiry_date, order.po_number);
        const isExpiredPo = poStat.isExpired;

        return (
            <TableRow
                key={order.id}
                onClick={() => canViewServiceOrders && navigate(`/service-orders/${order.display_name || order.id}`)}
                title={canViewServiceOrders ? `Click to view ${order.display_name || order.name || `SO-${order.id}`}` : ''}
                sx={{
                    cursor: canViewServiceOrders ? 'pointer' : 'default',
                    backgroundColor: isExpiredPo
                        ? 'rgba(239, 68, 68, 0.04)'
                        : isChildInGroup
                            ? `${theme.palette.grey[50]}`
                            : isOrphanChild
                                ? `${theme.palette.warning.main}08`
                                : isParentWithChildren
                                    ? `${theme.palette.primary.main}04`
                                    : 'inherit',
                    borderLeft: isExpiredPo
                        ? '4px solid #f87171'
                        : isChildInGroup
                            ? `3px solid ${theme.palette.primary.main}`
                            : isParentWithChildren
                                ? `3px solid ${theme.palette.primary.main}`
                                : '4px solid transparent',
                    '&:hover': canViewServiceOrders ? { bgcolor: isExpiredPo ? 'rgba(211, 47, 47, 0.13)' : 'action.hover' } : {}
                }}
            >
                <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }} title={order.display_name || order.name || `SO-${order.id}`}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pl: isChildInGroup ? 4 : 0
                    }}>
                        {/* Expand/collapse button for parents with children */}
                        {isParentWithChildren && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleParentExpand(order._displayName);
                                }}
                                sx={{ p: 0.25 }}
                            >
                                {expandedParents[order._displayName]
                                    ? <ExpandLessIcon fontSize="small" />
                                    : <ExpandMoreIcon fontSize="small" />
                                }
                            </IconButton>
                        )}
                        {/* Parent indicator with child count */}
                        {isParentWithChildren && (
                            <Tooltip title={`${childCount} sub-order${childCount > 1 ? 's' : ''}`}>
                                <Chip
                                    size="small"
                                    label={childCount}
                                    color="primary"
                                    sx={{
                                        minWidth: 24,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        '& .MuiChip-label': { px: 0.75 }
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Typography variant="body2" noWrap sx={{ fontWeight: order.name ? 600 : 400 }}>
                            {order.display_name || order.name || `SO-${order.id}`}
                        </Typography>
                        {isExpiredPo && (
                            <Tooltip title={`PO ${order.po_number || ''} expired on ${order.po_expiry_date || 'past date'} (${poStat.description})`}>
                                <Chip
                                    label="PO EXPIRED"
                                    size="small"
                                    sx={{
                                        ml: 0.5,
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.02em',
                                        backgroundColor: '#fee2e2',
                                        color: '#b91c1c',
                                        border: '1px solid #fca5a5'
                                    }}
                                />
                            </Tooltip>
                        )}
                        {poStat.isExpiringSoon && (
                            <Tooltip title={`PO ${order.po_number || ''} expires on ${order.po_expiry_date} (${poStat.description})`}>
                                <Chip
                                    label="PO EXPIRING"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 0.5, height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                />
                            </Tooltip>
                        )}
                        {/* Show parent reference for orphan sub-orders */}
                        {isOrphanChild && (order.parent_display_name || order.parent_order_id) && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                (Parent: {order.parent_display_name || `SO-${order.parent_order_id}`})
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                {canViewServiceOrders && <TableCell sx={{ maxWidth: 200 }}>
                    {order.service_type?.name ? (
                        <Chip label={order.service_type.name} size="small" variant="outlined" color="primary" />
                    ) : '-'}
                </TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 180 }} title={order.company?.name}>
                    {order.company ? (
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {order.company.name}
                        </Typography>
                    ) : '-'}
                </TableCell>
                <TableCell>
                    <Chip
                        label={formatStatusLabel(order.status?.name) || '-'}
                        color={getServiceOrderStatusChipColor(order.status?.name)}
                        size="small"
                    />
                </TableCell>
                {canViewServiceOrders && <TableCell>
                    <Chip
                        label={formatStatusLabel(order.priority?.name) || '-'}
                        color={getServiceOrderPriorityChipColor(order.priority?.name)}
                        size="small"
                    />
                </TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {order.technician
                        ? ((order.technician.first_name || order.technician.last_name)
                            ? `${order.technician.first_name || ''} ${order.technician.last_name || ''}`.trim()
                            : order.technician.email)
                        : 'Unassigned'}
                </TableCell>
                {canViewServiceOrders && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(order.scheduled_at)}
                </TableCell>}
                <TableCell onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                        {canViewServiceOrders && (
                            <Chip
                                icon={<ViewIcon sx={{ fontSize: 16 }} />}
                                label="View"
                                size="small"
                                onClick={() => navigate(`/service-orders/${order.display_name || order.id}`)}
                                clickable
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        )}
                        {canEditServiceOrders && (
                            <Chip
                                icon={<EditIcon sx={{ fontSize: 16 }} />}
                                label="Edit"
                                size="small"
                                onClick={() => navigate(`/service-orders/${order.display_name || order.id}/edit`)}
                                clickable
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                            />
                        )}
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <PageContainer>
            {appliedFilters.po_status === 'expired' && (
                <Alert
                    severity="error"
                    icon={<WarningAmberIcon />}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(211, 47, 47, 0.08)',
                        color: '#c62828',
                        border: '1px solid rgba(211, 47, 47, 0.25)',
                        alignItems: 'center',
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            <strong>Attention:</strong> Showing service orders with an <strong>expired Purchase Order</strong>.
                        </Typography>
                        <Chip
                            label="View All Orders"
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={clearFilters}
                            sx={{ cursor: 'pointer', fontWeight: 600, bgcolor: 'background.paper' }}
                        />
                    </Box>
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    <SecondaryButton
                        startIcon={<FilterIcon />}
                        onClick={handleToggleFilters}
                    >
                        {showFilters ? 'Hide filters' : 'Show filters'}
                    </SecondaryButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Sort:</Typography>
                        <CustomDropdown
                            size="small"
                            fullWidth={false}
                            value={appliedFilters.ordering}
                            onChange={(e) => handleSortChange(e.target.value)}
                            options={[
                                { value: '-created_at', label: 'Newest first' },
                                { value: 'created_at', label: 'Oldest first' },
                                { value: 'name', label: 'Name (A–Z)' },
                                { value: '-name', label: 'Name (Z–A)' },
                                { value: 'scheduled_at', label: 'Scheduled (asc)' },
                                { value: '-scheduled_at', label: 'Scheduled (desc)' },
                            ]}
                            sx={{ minWidth: 180 }}
                        />
                    </Box>
                </Box>
                {canAddServiceOrders && (
                    <PrimaryButton
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/service-orders/new')}
                    >
                        New service order
                    </PrimaryButton>
                )}
            </Box>

            {/* Search bar */}
            <Box sx={{ mb: 2 }}>
                <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClear={handleClearSearch}
                    inputRef={searchInputRef}
                    placeholder="Search by ID, Company, Customer, Service Type…"
                    startAdornment={loading && searchQuery ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        }
                    }}
                />
            </Box>

            {showFilters && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Status"
                                    value={draftFilters.status}
                                    onChange={(e) => handleDraftFilterChange('status', e.target.value)}
                                    options={statusOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => opt.name}
                                    emptyLabel="All"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Priority"
                                    value={draftFilters.priority}
                                    onChange={(e) => handleDraftFilterChange('priority', e.target.value)}
                                    options={priorityOptions}
                                    getOptionValue={(opt) => opt.id}
                                    getOptionLabel={(opt) => opt.name}
                                    emptyLabel="All"
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="Order type"
                                    value={draftFilters.is_sub_service_order}
                                    onChange={(e) => handleDraftFilterChange('is_sub_service_order', e.target.value)}
                                    options={[
                                        { value: '', label: 'All orders' },
                                        { value: 'false', label: 'Parent only' },
                                        { value: 'true', label: 'Sub-orders only' },
                                    ]}
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 250 }}>
                                <CustomDropdown
                                    label="PO status"
                                    value={draftFilters.po_status}
                                    onChange={(e) => handleDraftFilterChange('po_status', e.target.value)}
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: 'expired', label: 'PO Expired' },
                                        { value: 'expiring_soon', label: 'PO Expiring Soon' },
                                        { value: 'valid', label: 'PO Valid' },
                                        { value: 'none', label: 'No PO' },
                                    ]}
                                    fullWidth
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <PrimaryButton onClick={applyFilters} size="medium">
                                    Apply filters
                                </PrimaryButton>
                                <DestructiveButton
                                    startIcon={<ClearIcon />}
                                    onClick={clearFilters}
                                    size="medium"
                                >
                                    Clear filters
                                </DestructiveButton>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <TableContainer>
                <Table sx={{
                    '& .MuiTableCell-root': {
                        px: 1.5,
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiTableCell-head': {
                        fontWeight: 600,
                    }
                }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id}>{column.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {organizedOrders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No service orders found
                                </TableCell>
                            </TableRow>
                        ) : (
                            organizedOrders.map((order, index) => {
                                const rows = [renderOrderRow(order)];

                                // Add "Show More" row after the last visible child
                                if (order._showMoreAfterThis) {
                                    rows.push(
                                        <TableRow
                                            key={`show-more-${order._parentId}`}
                                            sx={{
                                                backgroundColor: theme.palette.grey[100],
                                                borderLeft: `3px solid ${theme.palette.primary.main}`,
                                            }}
                                        >
                                            <TableCell colSpan={8} sx={{ py: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        pl: 4,
                                                        cursor: 'pointer',
                                                        color: 'primary.main',
                                                        '&:hover': { textDecoration: 'underline' }
                                                    }}
                                                    onClick={() => navigate(`/service-orders/${order._parentDisplayName || order._parentId}#sub-service-orders`)}
                                                >
                                                    <MoreHorizIcon fontSize="small" />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        Show {order._remainingChildCount} more sub-orders
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                                return rows;
                            }).flat()
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && orders && orders.length > 0 && (
                <KQSPLPagination
                    count={pagination.count}
                    page={pagination.page}
                    pageSize={pagination.pageSize}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onRefresh={refresh}
                    disabled={loading}
                />
            )}

        </PageContainer>
    );
};

export default ServiceOrderList;
